import { downloadPgn, IDownloadPgnParams } from '@libs/chess/downloadPgn';
import { winnerToResult } from '@libs/chess/formatResults';
import { IPgnMove, Pgn } from '@libs/chess/pgn';
import { formatName } from '@libs/formatNames';

import { IGame } from '../types/game';
import { mapGameWinner } from './mapGameWinner';

export function downloadServerGamePgn(game: IGame) {
  downloadPgn({ ...mapDownloadPgnParams(game), moves: mapPgnMoves(game) });
}

export function mapDownloadPgnParams(game: IGame): Omit<IDownloadPgnParams, 'moves'> {
  const white = formatName(game.white);
  const black = formatName(game.black);

  return {
    filename: getGamePgnFilename(game),
    winner: mapGameWinner(game.result) ?? null,
    //
    link: window.location.href,
    site: window.location.origin,
    event: game.tournament?.name,
    date: game.startTimestampMs,
    round: game.round?.roundNumber,
    white,
    black,
    mode: 'ICS',
  };
}

export function getGamePgnFilename(game: IGame): string {
  const white = formatName(game.white);
  const black = formatName(game.black);
  const base = [
    game.tournament?.name.replace(/\.$/, ''),
    game.round ? `R${game.round.roundNumber}` : null,
    `${white} vs ${black}`,
  ]
    .filter(Boolean)
    .join('. ');
  return `${base}.pgn`;
}

export function mapGameToPgn(game: IGame): Pgn<IPgnMove> {
  const white = formatName(game.white);
  const black = formatName(game.black);
  const winner = mapGameWinner(game.result);
  const fen = game.moves[0]?.fenBefore ?? game.currentFen;

  const tags: Record<string, string> = {};
  if (game.tournament?.name) tags.Event = game.tournament.name;
  if (window.location.origin) tags.Site = window.location.origin;
  if (game.startTimestampMs) {
    tags.Date = new Date(game.startTimestampMs).toISOString().substring(0, 10).replaceAll('-', '.');
  }
  if (game.round?.roundNumber) tags.Round = String(game.round.roundNumber);
  if (white) tags.White = white;
  if (black) tags.Black = black;
  if (winner) tags.Result = winnerToResult(winner);
  if (window.location.href) tags.Link = window.location.href;
  if (fen) tags.FEN = fen;
  tags.Mode = 'ICS';

  return new Pgn({ tags, moves: mapPgnMoves(game) });
}

function mapPgnMoves(game: IGame): IPgnMove[] {
  return game.moves.map((m, i) => ({
    id: `${m.side}-${m.timestampMs}`,
    color: m.side === 'Black' ? 'black' : 'white',
    san: m.san,
    moveFull: mapFullMove(i + 1),
    timeRemaining: m.clockAfter
      ? Math.floor(m.clockAfter[m.side === 'White' ? 'whiteLeftMs' : 'blackLeftMs'] / 1000)
      : undefined,
  }));
}

function mapFullMove(halfmove: number): number {
  const x = halfmove % 2 === 0 ? 0 : -1;
  return (halfmove - x) / 2;
}
